import React from 'react';
import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import componentName from './componentName';

/**
 * An HOC to maintain state for a list of id'd items.
 * @param {Object} defaultState - state object to use when an item is first added.
 * @param {Object} mapPropsToInitialStateById (optional) - returns the initial state for each row given the
 *                                                         wrapped component's props
 * @param {Function} mapPropsToOrderedIds - returns a list of ordered ids given the wrapped component's props
 * @param {Function} mapStateToProps - returns the props to pass to the wrapped component,
 *                                     given args: rowStates, update(id, op), updateAll(op)
 * @return {Function} - a function to wrap the component class that has the list of items
 */
export default function manageRowState({
  defaultState,
  mapPropsToInitialStateById,
  mapPropsToOrderedIds,
  mapStateToProps,
}) {
  function makeComponent(WrappedComponent) {
    return class extends React.PureComponent {
      static displayName = `RowStateManager(${componentName(WrappedComponent)})`;

      UNSAFE_componentWillReceiveProps(nextProps) {
        const oldIds = mapPropsToOrderedIds(this.props);
        const newIds = mapPropsToOrderedIds(nextProps);
        if (!isEqual(oldIds, newIds)) {
          this.setState({
            rowStates: this._makeRowStates(nextProps, this.state.rowStates),
          });
        }
      }

      _makeRowStates = (nextProps, oldStates = []) => {
        const ids = mapPropsToOrderedIds(nextProps);
        const oldStatesById = _.indexBy(oldStates, 'id');
        const initialStateById = mapPropsToInitialStateById ? mapPropsToInitialStateById(nextProps) : {};
        const rowStates = ids.map((id) => {
          const initialRowState = (initialStateById && initialStateById[id]) || defaultState;
          return {...(oldStatesById[id] || initialRowState), id};
        });
        return rowStates;
      };

      _update = (id, transformRowState) => {
        const index = this.state.rowStates.findIndex((item) => item.id === id);
        this.setState({
          rowStates: reactUpdate(this.state.rowStates, {[index]: {$apply: transformRowState}}),
        });
      };

      _updateEach = (transformRowState) => {
        this.setState({
          rowStates: this.state.rowStates.map((value) => transformRowState(value)),
        });
      };

      state = {
        rowStates: this._makeRowStates(this.props),
      };

      render() {
        return (
          <WrappedComponent
            {...this.props}
            {...mapStateToProps(this.state.rowStates, this._update, this._updateEach)}
          />
        );
      }
    };
  }
  return makeComponent;
}
